import React from "react";
import { Link, useOutletContext } from "react-router";
import { useRouteParams } from "@/hooks/useEncodedIds";
import {
  MarketplaceContext,
  useGetMarketplaceCategoryOffers,
  useGetOffersWithoutCategory,
  Vendor,
  VendorOffer
} from "@/context/MarketplaceLayout";
import { Spinner } from "@/components/Spinner";
import Layout from "@/components/Layout";
import { trackMarketplaceOfferClick } from "@/utils/analytics";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const MarketplaceCategory = () => {
  const { leaseId, categoryId } = useRouteParams<{
    leaseId: string;
    categoryId: string;
  }>();
  const { isLoading: isFetching, categories } =
    useOutletContext<MarketplaceContext>();

  // Conditionally call the hooks based on whether categoryId exists
  const categoryOffersQuery = useGetMarketplaceCategoryOffers(
    leaseId!,
    categoryId!
  );

  const offersWithoutCategoryQuery = useGetOffersWithoutCategory(leaseId!);

  // Use the appropriate query based on categoryId
  const offers =
    categoryId !== "null"
      ? categoryOffersQuery.data || []
      : offersWithoutCategoryQuery.data || [];
  const isLoading =
    categoryId !== "null"
      ? categoryOffersQuery.isLoading
      : offersWithoutCategoryQuery.isLoading;

  if (isLoading || isFetching) {
    return (
      <Layout title="Category Offers">
        <div className="flex items-center justify-center flex-1 my-56">
          <Spinner color="livly" size="xl" />
        </div>
      </Layout>
    );
  }

  const category = categories.find(
    (cat) => cat.externalId === Number(categoryId)
  );

  return (
    <Layout
      title="Category Offers"
      back={{
        to: -1,
        label: "Building Offers"
      }}
    >
      {/* add category name */}
      <div>
        <h1 className="text-3xl font-medium mb-4">
          {category?.title || "Other"}
        </h1>
      </div>
      <div className="mt-4">
        {offers.length > 0 ? (
          <div className="grid grid-cols-1 gap-6">
            {offers.map((offer: Vendor, index: number) => (
              <VendorItem key={index} vendor={offer} />
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center flex-1 my-56">
            <p className="text-gray-500">
              No offers available for this category
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};

function VendorItem({ vendor }: { vendor: Vendor }) {
  return (
    <li className="mb-8 list-none">
      <div className="mb-2">
        <>
          {vendor.vendorSubtitle && (
            <p className="font-medium text-lg mb-2">{vendor.vendorSubtitle}</p>
          )}
          <div className="flex gap-1 text-gray-500 items-center">
            <p>Powered by</p>
            {vendor.vendorLogo ? (
              <img
                src={vendor.vendorLogo}
                alt={vendor.vendorName}
                className="h-6"
              />
            ) : (
              <h5>{vendor.vendorName}</h5>
            )}
          </div>
        </>
      </div>
      <div className="flex w-full pb-4 overflow-x-auto overflow-y-hidden flex-nowrap">
        {vendor.vendorOffers.map((offer: VendorOffer, offerIndex: number) => (
          <VendorOfferItem
            key={offerIndex}
            offer={offer}
            vendor={vendor}
            trackMarketplaceOfferClick={() => {
              trackMarketplaceOfferClick(vendor.vendorOffers[0].offerTitle);
            }}
          />
        ))}
      </div>
    </li>
  );
}

function VendorOfferItem({
  vendor,
  offer,
  trackMarketplaceOfferClick
}: {
  vendor: Vendor;
  offer: VendorOffer;
  trackMarketplaceOfferClick: (title: string) => void;
}) {
  return (
    <div className="flex flex-col flex-shrink-0 mr-4 w-48">
      <Link
        to={`../marketplace/${vendor._id}/${offer._id}`}
        className="flex mb-2"
        onClick={() => {
          trackMarketplaceOfferClick(offer.offerTitle);
        }}
      >
        {offer.offerSquareImage ? (
          <img
            className="w-[200px] h-[200px] object-cover bg-no-repeat bg-center"
            src={offer.offerSquareImage}
            alt={offer.offerSubTitle}
          />
        ) : (
          <div className="flex items-center justify-center bg-gray-200 w-[200px] h-[200px]">
            <FontAwesomeIcon icon="image" size="lg" className="text-gray-500" />
          </div>
        )}
      </Link>
      <div className="mb-4">
        <p className="mb-2 text-xs text-gray-500">{vendor.vendorName}</p>
        <p className="leading-none">{offer.offerTitle}</p>
      </div>
      <Link to={`../marketplace/${vendor._id}/${offer._id}`}>
        <p className="text-sm font-semibold text-red-400">
          {vendor.vendorCtaText}
        </p>
      </Link>
    </div>
  );
}

export default MarketplaceCategory;
